/* AppraisalComponents/SheetSectionAdornments.jsx
 * Phase C · C4c Sheet extraction (2026-02c).
 *
 * Presentational section-body adornments for Sheet:
 *   • SheetSectionProfilePicker — profile <select> + Reset-to-roof-details
 *   • SheetSectionTotals        — running Lineal + m² totals with
 *                                  vs-Roof-Details tolerance badge
 *
 * Both are pure props-in JSX-out · no React state · no side effects.
 * Extracted from AppraisalComponentsTab.js:3967-4072.
 */
import React from 'react';

export function SheetSectionProfilePicker({
  sheetSectionProfile,
  setSheetSectionProfile,
  sheetProfileNames,
  roofProfile,
  setGroups,
  setDirty,
}) {
  return (
    <div
      data-testid="sheet-section-profile"
      className="rounded-md border border-slate-200 bg-white px-3 py-2 flex items-center gap-2 flex-wrap"
    >
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
        Roof Profile
      </span>
      <select
        data-testid="sheet-section-profile-select"
        value={sheetSectionProfile}
        onChange={(e) => {
          const next = e.target.value;
          setSheetSectionProfile(next);
          // Auto-cascade to every Sheet row in one click.
          setGroups(prev => {
            const sheets = prev.Sheet || [];
            if (!sheets.length) return prev;
            const changed = sheets.some(l => l.profile !== next);
            if (!changed) return prev;
            setDirty(true);
            return { ...prev, Sheet: sheets.map(l => ({ ...l, profile: next })) };
          });
        }}
        className="text-xs border border-slate-300 rounded px-2 py-1 font-semibold"
      >
        <option value="">— Any profile —</option>
        {sheetProfileNames.map(p => (
          <option key={p} value={p}>{p}</option>
        ))}
      </select>
      {roofProfile && sheetSectionProfile !== roofProfile && (
        <button
          type="button"
          data-testid="sheet-section-profile-reset"
          onClick={() => setSheetSectionProfile(roofProfile)}
          className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-50"
          title={`Reset to roof_details profile (${roofProfile})`}
        >
          Reset to Roof Details
        </button>
      )}
      <span className="ml-auto text-[10px] text-slate-400 italic">
        From Roof Details: <span className="font-semibold text-slate-600 not-italic">{roofProfile || '—'}</span>
        {sheetSectionProfile && (
          <> · narrows Type to rows containing <strong className="text-slate-800">{sheetSectionProfile}</strong></>
        )}
      </span>
    </div>
  );
}

// Iter191 · Running Lineal + m² totals across every Sheet row, with a
// tolerance badge vs Roof Details m².
// Sprint S3 (2026-02c) · Sheets Face-M² Bridge — tolerance swapped
// from a fixed 1.5 m² band to a percentage-based flag (default 5 %)
// so it scales with job size. Matches the backend's
// `resolve_roof_m2_variance` tolerance so both surfaces agree.
const SHEETS_TOLERANCE_PCT = 5.0;

export function SheetSectionTotals({ sheetLines, roofM2 }) {
  if (!sheetLines || sheetLines.length === 0) return null;
  let totalLineal = 0;
  let totalSqm = 0;
  sheetLines.forEach(l => {
    const lineal = Number(l.length) > 0
      ? Number(l.quantity) * Number(l.length)
      : Number(l.quantity);
    totalLineal += lineal;
    totalSqm += lineal * (Number(l.cover_width) || 0);
  });
  const rdM2 = Number(roofM2) || 0;
  const delta = rdM2 > 0 ? totalSqm - rdM2 : 0;
  const variancePct = rdM2 > 0 ? Math.abs(delta) / rdM2 * 100 : 0;
  const within = rdM2 === 0 || variancePct <= SHEETS_TOLERANCE_PCT;
  const beyondTwoX = variancePct > SHEETS_TOLERANCE_PCT * 2;
  const deltaLabel = rdM2 > 0
    ? (within
        ? `Match · ${variancePct.toFixed(1)}%`
        : (delta > 0
          ? `+${delta.toFixed(2)} m² · ${variancePct.toFixed(1)}% over`
          : `${delta.toFixed(2)} m² · ${variancePct.toFixed(1)}% shortfall`))
    : '';
  return (
    <div
      data-testid="sheet-section-totals"
      className="rounded-md border border-emerald-200 bg-emerald-50/40 px-3 py-2 flex items-center gap-4 flex-wrap"
    >
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">
        Running Totals
      </span>
      <div className="flex items-baseline gap-1.5">
        <span className="text-[10px] text-slate-500 uppercase tracking-wider">Roof Mtr Sheets</span>
        <span data-testid="sheet-totals-lineal" className="text-base font-bold text-slate-900 font-mono-num">
          {totalLineal.toFixed(2)}
        </span>
        <span className="text-[10px] text-slate-400">m</span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-[10px] text-slate-500 uppercase tracking-wider">Roof m² Sheets</span>
        <span data-testid="sheet-totals-sqm" className="text-base font-bold text-slate-900 font-mono-num">
          {totalSqm.toFixed(2)}
        </span>
        <span className="text-[10px] text-slate-400">m²</span>
      </div>
      {rdM2 > 0 && (
        <div
          data-testid="sheet-totals-vs-roof-details"
          className={`flex items-baseline gap-1.5 px-2 py-1 rounded font-mono-num ${
            within ? 'bg-emerald-100 text-emerald-800'
            : beyondTwoX ? 'bg-rose-100 text-rose-800'
            : 'bg-amber-100 text-amber-800'
          }`}
          title={`Roof Details quotes ${rdM2.toFixed(2)} m². Your Sheet rows total ${totalSqm.toFixed(2)} m² (${variancePct.toFixed(1)}% variance · tolerance ±${SHEETS_TOLERANCE_PCT}%). ${
            within ? 'Within tolerance.'
            : delta > 0 ? 'Sheets exceed Roof Details — could indicate double-counted faces, over-ordered cover, or expected off-cut wastage from hips/valleys.'
            : 'Sheets fall short of Roof Details — could mean a face is missing or your Cover/Length is off.'
          }`}
        >
          <span className="text-[10px] uppercase tracking-wider opacity-80">vs Roof Details</span>
          <span className="text-xs font-bold">{rdM2.toFixed(2)} m²</span>
          <span className="text-[10px] font-bold uppercase">· {deltaLabel}</span>
        </div>
      )}
      <span className="ml-auto text-[10px] text-slate-400 italic">
        {sheetLines.length} row{sheetLines.length === 1 ? '' : 's'} · Lineal = qty × length · m² = lineal × cover
      </span>
    </div>
  );
}
